// P4 — Statistiques responsable avec 4 vrais graphes (Recharts)
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/lib/i18n";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";

interface ReportingData {
  interventions_by_month: Array<{
    month: string;
    preventive: number;
    curative: number;
  }>;
  costs_by_engin: Array<{ engin_code: string; cost: number }>;
  avg_delay_by_month: Array<{ month: string; avg_delay_hours: number }>;
  stock_alerts: Array<{
    article_ref: string;
    current: number;
    min: number;
  }>;
}

const PIE_COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
];

export default function StatistiquesPage() {
  const t = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async (): Promise<ReportingData> => {
      const r = await fetch(`${API_BASE}/v1/dashboard/stats`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`API ${r.status}`);
      return r.json();
    },
  });

  if (isLoading) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-bold tracking-tight">{ht("Statistiques Responsable")}
        </h1>
        <p className="mt-4 text-muted-foreground">{ht("Chargement des données...")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-bold tracking-tight">{ht("Statistiques Responsable")}
        </h1>
        <p className="mt-4 text-red-600">{t("common.error")} : {(error as Error).message}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{ht("Statistiques Responsable")}
        </h1>
        <p className="text-muted-foreground">
          Vue globale du module Maintenance — 6 derniers mois
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Graph 1 : BarChart Interventions par type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Interventions par type (6 derniers mois)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.interventions_by_month.length === 0 ? (
              <EmptyState message={t("stats.empty.interventions")} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.interventions_by_month}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="preventive"
                    stackId="a"
                    fill="#22c55e"
                    name={t("stats.preventive")}
                  />
                  <Bar
                    dataKey="curative"
                    stackId="a"
                    fill="#ea580c"
                    name={t("stats.curative")}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 2 : PieChart Coûts par engin (top 10) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("stats.costs_by_engin")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.costs_by_engin.length === 0 ? (
              <EmptyState message={t("stats.empty.costs")} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={data.costs_by_engin}
                    dataKey="cost"
                    nameKey="engin_code"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {data.costs_by_engin.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={PIE_COLORS[idx % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) =>
                      `${value.toLocaleString("fr-FR")} FCFA`
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 3 : LineChart Délai moyen */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{ht("Délai moyen d'intervention (heures)")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.avg_delay_by_month.length === 0 ? (
              <EmptyState message={t("stats.empty.delay")} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.avg_delay_by_month}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis
                    label={{
                      value: "heures",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip
                    formatter={(v: number) =>
                      `${v.toFixed(1)} h`
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_delay_hours"
                    stroke="#3b82f6"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Graph 4 : BarChart Stock articles alertes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("stats.stock_alerts")} (current &lt; min)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.stock_alerts.length === 0 ? (
              <EmptyState message={t("stats.empty.stock")} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={data.stock_alerts.map((a) => ({
                    article: a.article_ref,
                    current: a.current,
                    min: a.min,
                    manque: Math.max(0, a.min - a.current),
                  }))}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="article" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="current" fill="#ef4444" name={t("stats.stock_current")} />
                  <Bar dataKey="min" fill="#9ca3af" name={t("stats.stock_min")} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded border-2 border-dashed border-border bg-muted/20">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}